import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';
import {
  createStorageMetadata,
  recordDocumentMetadata,
  type DocumentUploadContext,
} from './documentRegistry';
import { sanitizeFileName, sanitizeStoragePath } from '@/utils/fileNameSanitizer';

export interface TaskReferenceImageUploadContext {
  taskId: string;
  organizationId?: string;
  locationId?: string;
  uploadedBy?: {
    id: string;
    email: string;
    name: string;
  };
}

export interface StorageUploadResult {
  url: string;
  storagePath: string;
}

export interface ReferenceImageMetadata {
  url: string;
  fileName: string;
  uploadedAt: Date | number;
  uploadedBy: string;
  uploadedByName?: string;
}

// Maximum file size: 10MB per image
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

// Allowed image MIME types
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

/**
 * Validate image file before upload
 */
function validateImageFile(file: File): void {
  // Check file type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(
      `Invalid file type: ${file.type}. Allowed types: JPEG, PNG, WebP, GIF`
    );
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    const maxSizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
    throw new Error(
      `File "${file.name}" is too large (${fileSizeMB}MB). Maximum file size is ${maxSizeMB}MB.`
    );
  }
}

/**
 * Upload a single reference image for a task
 */
export async function uploadTaskReferenceImage(
  file: File,
  context: TaskReferenceImageUploadContext
): Promise<StorageUploadResult> {
  console.log('🔵 uploadTaskReferenceImage called with:', file.name, file.size, 'bytes');

  // Validate file
  validateImageFile(file);

  if (!storage) {
    console.error('❌ Firebase Storage not initialized');
    throw new Error('Firebase Storage not initialized. Please check your Firebase configuration.');
  }

  try {
    const uploadedAt = new Date().toISOString();
    const timestamp = Date.now();
    const sanitizedFileName = sanitizeFileName(file.name) || 'image';
    const fileExtension = file.name.split('.').pop() || 'jpg';
    const fileName = `${timestamp}_${sanitizedFileName}.${fileExtension}`;
    
    // Storage path: task_reference_images/{taskId}/{timestamp}_{filename}
    const storagePath = `task_reference_images/${context.taskId}/${fileName}`;
    console.log('📝 Creating storage reference:', storagePath);
    
    // Create a storage reference
    const storageRef = ref(storage, storagePath);
    
    const storageContext: DocumentUploadContext = {
      docType: 'task_reference_image',
      organizationId: context.organizationId,
      locationId: context.locationId,
      taskId: context.taskId,
      uploadedBy: context.uploadedBy,
      title: `Task Reference Image - ${file.name}`,
      source: 'web',
    };

    // Upload the file
    console.log('⬆️ Uploading file to Firebase Storage...');
    await uploadBytes(
      storageRef,
      file,
      createStorageMetadata(storageContext, {
        contentType: file.type,
        uploadedAt,
      })
    );
    console.log('✅ File bytes uploaded successfully');
    
    // Get the download URL
    console.log('🔗 Getting download URL...');
    const downloadURL = await getDownloadURL(storageRef);
    console.log('✅ Download URL:', downloadURL);
    
    // Record metadata in document registry
    await recordDocumentMetadata({
      storagePath: storageRef.fullPath,
      bucket: (storageRef as any).bucket,
      downloadUrl: downloadURL,
      mimeType: file.type,
      sizeBytes: file.size,
      uploadedAt,
      context: storageContext,
    });

    return { url: downloadURL, storagePath: storageRef.fullPath };
  } catch (error: any) {
    console.error('❌ Error uploading reference image:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    
    // Provide helpful error messages
    if (error.code === 'storage/unauthorized' || error.code === 'storage/permission-denied') {
      throw new Error('Permission denied. Please check Firebase Storage rules.');
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error('Storage quota exceeded. Please contact administrator.');
    } else if (error.code === 'storage/unknown') {
      throw new Error('Unknown storage error. Please try again.');
    } else {
      throw new Error(`Image upload failed: ${error.message}`);
    }
  }
}

/**
 * Upload multiple reference images for a task
 */
export async function uploadMultipleTaskReferenceImages(
  files: File[],
  context: TaskReferenceImageUploadContext
): Promise<StorageUploadResult[]> {
  console.log(`🔵 uploadMultipleTaskReferenceImages called with ${files.length} file(s)`);

  // Validate all files first
  files.forEach((file) => {
    validateImageFile(file);
  });

  // Check total size (optional: limit total size)
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const maxTotalSize = 50 * 1024 * 1024; // 50MB total
  if (totalSize > maxTotalSize) {
    const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
    const maxTotalSizeMB = (maxTotalSize / (1024 * 1024)).toFixed(0);
    throw new Error(
      `Total file size (${totalSizeMB}MB) exceeds maximum (${maxTotalSizeMB}MB). Please reduce the number or size of images.`
    );
  }

  // Upload all files in parallel
  const uploadPromises = files.map((file) =>
    uploadTaskReferenceImage(file, context)
  );

  try {
    const results = await Promise.all(uploadPromises);
    console.log(`✅ Successfully uploaded ${results.length} reference image(s)`);
    return results;
  } catch (error: any) {
    console.error('❌ Error uploading multiple reference images:', error);
    throw error;
  }
}

/**
 * Delete a reference image from storage
 */
export async function deleteTaskReferenceImage(
  imageUrl: string,
  taskId: string
): Promise<void> {
  if (!storage) {
    throw new Error('Firebase Storage not initialized');
  }

  try {
    // Extract storage path from URL or construct it
    // Firebase Storage URLs have format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media
    const urlObj = new URL(imageUrl);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
    
    if (!pathMatch) {
      throw new Error('Invalid image URL format');
    }

    // Decode the path (URL encoded)
    const encodedPath = pathMatch[1];
    const storagePath = decodeURIComponent(encodedPath);
    
    console.log('🗑️ Deleting reference image from:', storagePath);
    
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    
    console.log('✅ Reference image deleted successfully');
  } catch (error: any) {
    console.error('❌ Error deleting reference image:', error);
    if (error.code === 'storage/object-not-found') {
      console.warn('⚠️ Image not found in storage (may have been already deleted)');
      // Don't throw error if image doesn't exist
      return;
    }
    throw new Error(`Failed to delete reference image: ${error.message}`);
  }
}

/**
 * Create reference image metadata object
 */
export function createReferenceImageMetadata(
  uploadResult: StorageUploadResult,
  fileName: string,
  uploadedBy: { id: string; email: string; name?: string }
): ReferenceImageMetadata {
  return {
    url: uploadResult.url,
    fileName: fileName,
    uploadedAt: Date.now(),
    uploadedBy: uploadedBy.id,
    uploadedByName: uploadedBy.name || uploadedBy.email,
  };
}

